import { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { useConfirm } from '@components/ConfirmProvider'
import { toast } from '@src/helpers/toaster'
import { isSystemAdminRole } from '@models/Role'

import type { Role } from '@models/Role'

/** Minimum shape the exclusivity rule needs. Satisfied by both `Role`
 *  (the full role list) and `RoleInfo` (a member's assigned roles). */
export type ExclusiveRoleLike = Pick<Role, 'id' | 'name' | 'is_system'>

export interface AdminExclusiveRoles {
  /** Id of the team's system Admin role, when it has loaded. */
  adminRoleId?: number
  /** True when the current selection holds the system Admin role. */
  isAdminSelected: (selectedIds: number[]) => boolean
  /**
   * Next selection after toggling `role`, or `null` when the user cancels
   * the Admin swap (leave state untouched in that case).
   */
  resolveToggle: (selectedIds: number[], role: ExclusiveRoleLike) => Promise<number[] | null>
}

/**
 * Shared Admin-exclusivity rule for every role picker (member card,
 * members-list chips, bulk assign). The backend rejects Admin combined
 * with any other role, so the UI must never build such a set.
 *
 * Rather than locking the other options (which reads as a broken UI —
 * clicks that silently do nothing), the toggle SWAPS, asymmetrically:
 *
 *   - Picking Admin collapses the selection to Admin alone. No prompt:
 *     Admin is a superset, so no capability is lost. A toast explains why
 *     the other chips disappeared.
 *   - Picking another role while Admin is held is a demotion, so it
 *     confirms first and drops Admin on approval.
 *   - Deselecting anything is always allowed, unprompted.
 *
 * Only the SYSTEM Admin role is exclusive; a custom role a customer
 * happens to name "Admin" combines freely (matches `Role.is_admin_role`).
 */
export const useAdminExclusiveRoles = (allRoles?: ExclusiveRoleLike[]): AdminExclusiveRoles => {
  const { t } = useTranslation('org')
  const confirm = useConfirm()

  const adminRoleId = useMemo(() => allRoles?.find(isSystemAdminRole)?.id, [allRoles])

  const isAdminSelected = useCallback(
    (selectedIds: number[]) => adminRoleId !== undefined && selectedIds.includes(adminRoleId),
    [adminRoleId],
  )

  const resolveToggle = useCallback(
    async (selectedIds: number[], role: ExclusiveRoleLike): Promise<number[] | null> => {
      // Deselection can't create an Admin-plus-other combination.
      if (selectedIds.includes(role.id)) {
        return selectedIds.filter((id) => id !== role.id)
      }

      if (isSystemAdminRole(role)) {
        if (selectedIds.length > 0) {
          toast.info(t('adminExclusive.collapsedNote') as string)
        }
        return [role.id]
      }

      if (adminRoleId !== undefined && selectedIds.includes(adminRoleId)) {
        const approved = await confirm({
          title: t('adminExclusive.swapConfirmTitle') as string,
          message: t('adminExclusive.swapConfirmMessage', { role: role.name }) as string,
        })
        if (!approved) return null
        return [...selectedIds.filter((id) => id !== adminRoleId), role.id]
      }

      return [...selectedIds, role.id]
    },
    [adminRoleId, confirm, t],
  )

  return { adminRoleId, isAdminSelected, resolveToggle }
}

export default useAdminExclusiveRoles
